/**
 * Defensive Movement — two-phase recovery after DI knockback.
 *
 * Air Recovery: free hop/lift/burst, no timer, no drain. Ends on landing.
 * Landing Bar: damage-scaled walk-only timer, constant drain. Ends when depleted.
 *
 * Sequential: one defender at a time, all others frozen.
 */

import { Keys } from 'bonkjs';
import { DEFENSE, PHYSICS, MOVEMENT, TRACTION, TURN_FLOW, BURST, ACTION, TUMBLE, HOP } from '../constants';
import { getSurfaceNormal, isPointInTerrain, getGroundYBelow } from '../terrain/grid';
import { calculateMoveCost } from '../physics/movement';
import { clampToWorldX } from '../physics/tanks';
import { startBurstWindow } from './burst';
import { msToFrames } from './input-edge';
import { applyRecoveryAirControl } from './movement';
import { applyBurstTumbleDI, applyBurstAirControl } from './burst-di';
import type { GameState, TankActionState } from '../types';
import type { InputProvider } from '../input';
import type { GameEffect } from './effects';
import { transitionPhase } from './effects';
import { mirrorWalking } from '../physics/transitions';
import { updateHopLift, updateFastFall } from './hop';
import { lookupTank } from '../tanks';

/**
 * Compute landing bar duration in frames. Higher damage = shorter bar.
 * Tank landingMultiplier adjusts per-tank (Duelist longer, Launcher shorter).
 */
function computeLandingDuration(damagePercent: number, tankId: string): number {
  const tankMult = lookupTank(tankId).landingMultiplier ?? 1.0;
  const damageMult = Math.max(DEFENSE.LANDING_DAMAGE_FLOOR, 1 - damagePercent / DEFENSE.LANDING_DAMAGE_DIVISOR);
  return Math.round(DEFENSE.LANDING_BASE_FRAMES * tankMult * damageMult);
}

/**
 * Start the defensive movement phase for a set of knocked players.
 * Called from phases.ts after DI completion.
 */
export function startDefensivePhase(
  state: GameState,
  knockedIndices: number[],
  fx: GameEffect[],
): void {
  state.defenseQueue = [...knockedIndices];
  activateNextDefender(state, fx);
}

/**
 * Pop next defender from queue, set air sub-phase, freeze all others.
 */
export function activateNextDefender(state: GameState, fx: GameEffect[]): void {
  if (state.defenseQueue.length === 0) {
    state.defenseActivePlayer = -1;
    return;
  }

  const playerIndex = state.defenseQueue.shift()!;
  state.defenseActivePlayer = playerIndex;

  const p = state.players[playerIndex];

  // If this defender was previously frozen (multi-defender queue), restore their
  // actionState from the current freeze map before it's replaced with a new one.
  // Without this, the defender's actionState stays 'defensive_frozen' which causes
  // mirrorAirborne to overwrite it in the physics no-contact path, creating a
  // tag='airborne' / tumble=true mismatch.
  const prevSaved = state.defensePreFreezeStates.get(playerIndex);
  if (prevSaved) {
    p.actionState = prevSaved;
  }

  // Start in air sub-phase — landing bar computed when player touches ground
  p.defenseSubPhase = 'air';
  p.landingTimer = 0;
  p.landingTimerMax = 0;

  // Snapshot and freeze all other alive players
  state.defensePreFreezeStates = new Map();
  for (let i = 0; i < state.players.length; i++) {
    if (i === playerIndex) continue;
    const other = state.players[i];
    if (!other.alive) continue;
    // Snapshot current action state
    state.defensePreFreezeStates.set(i, { ...other.actionState } as TankActionState);
    // Freeze: zero velocity, set defensive_frozen
    other.vx = 0;
    other.vy = 0;
    other.actionState = { tag: 'defensive_frozen' };
  }

  // Camera to defender
  fx.push({ type: 'camera_follow', x: p.x, y: p.y });

  // Brief freeze for transition
  state.freezeUntil = Math.max(
    state.freezeUntil,
    state.frameCount + msToFrames(DEFENSE.PHASE_TRANSITION_FREEZE_MS),
  );

  // Initialize brake window
  p.brakeWindowFrames = DEFENSE.BRAKE_WINDOW_FRAMES;
  p.brakeUsed = false;

  // Entry effects — BIG moment (B2.3)
  fx.push({ type: 'screen_flash', color: p.color, alpha: 0.25 });
  fx.push({ type: 'announce_screen', text: 'RECOVER!', color: p.color, fontSize: 80, hold: 0.8 });
  fx.push({ type: 'particles', method: 'spawnDefenseEntry', args: [p.x, p.y, p.color] });
  fx.push({ type: 'screen_shake', intensity: 12, decay: 0.85 });
  fx.push({ type: 'defense_start', playerIndex });
}

/**
 * Unfreeze all players — restore action states from snapshots.
 */
export function unfreezeAllPlayers(state: GameState): void {
  for (const [i, savedState] of state.defensePreFreezeStates) {
    const p = state.players[i];
    if (!p.alive) continue;
    p.actionState = savedState;
  }
  state.defensePreFreezeStates = new Map();
}

/**
 * Per-frame defensive movement handler.
 * Called from the phase switch in phases.ts.
 * Routes to air or landing sub-phase handler.
 */
export function handleDefensiveMovement(
  state: GameState,
  input: InputProvider,
  fx: GameEffect[],
): void {
  // Skip while frozen
  if (state.frameCount < state.freezeUntil) return;

  const pi = state.defenseActivePlayer;
  if (pi < 0) {
    // No active defender — shouldn't happen, but safety transition
    unfreezeAllPlayers(state);
    transitionPhase(state, 'awaiting_next_turn', fx);
    state.turnTimer = TURN_FLOW.TURN_DELAY;
    return;
  }

  const player = state.players[pi];

  // Camera follows defender
  fx.push({ type: 'camera_follow', x: player.x, y: player.y });

  if (player.defenseSubPhase === 'air') {
    handleAirPhase(state, pi, player, input, fx);
  } else if (player.defenseSubPhase === 'landing') {
    handleLandingPhase(state, pi, player, input, fx);
  }
}

/**
 * Air sub-phase: free hop/lift/burst, airborne strafe, recovery air control.
 * No timer, no drain. Transitions to landing when grounded + not tumbling.
 */
function handleAirPhase(
  state: GameState,
  pi: number,
  player: import('../types').Player,
  input: InputProvider,
  fx: GameEffect[],
): void {
  // --- Brake window (momentum negate) ---
  if (player.brakeWindowFrames > 0 && !player.brakeUsed) {
    player.brakeWindowFrames--;
    const jumpDown = input.getKey(ACTION.JUMP);
    if (jumpDown) {
      player.brakeUsed = true;
      player.vx = 0;
      player.vy = 0;
      player.brakeWindowFrames = 0;
      // BREAK! explosion
      fx.push({ type: 'announce_screen', text: 'BREAK!', color: DEFENSE.BRAKE_FLASH_COLOR, fontSize: 64, hold: DEFENSE.BRAKE_ANNOUNCE_HOLD });
      fx.push({ type: 'screen_flash', color: DEFENSE.BRAKE_FLASH_COLOR, alpha: 0.3 });
      fx.push({ type: 'screen_shake', intensity: DEFENSE.BRAKE_SHAKE, decay: DEFENSE.BRAKE_SHAKE_DECAY });
      fx.push({ type: 'tank_flash', playerIndex: pi, bright: 0xffffff, dark: DEFENSE.BRAKE_DARK, duration: 0.4 });
      fx.push({ type: 'particles', method: 'spawnBrakeExplosion', args: [player.x, player.y, player.color] });
      fx.push({ type: 'brake_triggered', playerIndex: pi });
    }
    // During brake window, emit pulse VFX each frame
    if (!player.brakeUsed) {
      fx.push({ type: 'particles', method: 'spawnBrakeWindowPulse', args: [player.x, player.y, player.color, player.brakeWindowFrames / DEFENSE.BRAKE_WINDOW_FRAMES] });
    }
    return;
  }

  // --- Special (burst/brace from defense) — air phase only ---
  // Only authority detects and triggers special; non-authority receives
  // the explicit special_trigger message via handlers-gameplay.ts.
  if (!state.defenseWaitForAuthority) {
    const specialDown = input.getKey(ACTION.SPECIAL);
    const specialJustPressed = specialDown && !player.specialHeld;
    player.specialHeld = specialDown;

    if (specialJustPressed && !player.specialUsed && !player.tumble) {
      player.specialUsed = true;
      state.burstFromDefense = true;
      state.burstFromSpecial = false; // not from offensive special

      startBurstWindow(state, pi);
      transitionPhase(state, 'burst_window', fx);
      fx.push({ type: 'special_triggered', playerIndex: pi });
      fx.push({ type: 'burst_show' });
      fx.push({ type: 'screen_flash', color: BURST.ENTRY_FLASH_COLOR, alpha: BURST.ENTRY_FLASH_ALPHA });
      fx.push({ type: 'screen_shake', intensity: BURST.ENTRY_SHAKE, decay: BURST.ENTRY_SHAKE_DECAY });
      fx.push({ type: 'particles', method: 'spawnBurstEntry', args: [player.x, player.y, player.color] });
      return;
    }
  }

  // --- Hop & Lift (cost-free during air phase) ---
  const feetY = player.y + PHYSICS.ECB_RADIUS;
  const groundBelow = getGroundYBelow(player.x, feetY);
  const nearGround = !player.grounded && (groundBelow - feetY) < HOP.NEAR_GROUND_PX;
  const hopResult = updateHopLift(player, input, true, ACTION.JUMP, nearGround);
  if (hopResult.hopped) {
    fx.push({ type: 'particles', method: 'spawnHopDust', args: [player.x, player.y + PHYSICS.ECB_RADIUS, player.color] });
    fx.push({ type: 'screen_shake', intensity: 3, decay: 0.75 });
  }
  if (hopResult.liftBurst) {
    fx.push({ type: 'particles', method: 'spawnLiftBurst', args: [player.x, player.y, player.vx, player.vy, player.color] });
    fx.push({ type: 'screen_shake', intensity: 2.5, decay: 0.78 });
  }

  // --- Fast Fall (cost-free during air phase) ---
  const fastFallResult = updateFastFall(player, input, true);
  if (fastFallResult.activated) {
    fx.push({ type: 'particles', method: 'spawnFastFallActivation', args: [player.x, player.y, player.color] });
  }

  // --- Recovery air control during tumble recovery ---
  applyRecoveryAirControl(player, input);

  // --- Burst tumble DI — velocity influence during post-burst hitstun ---
  if (player.tumble && player.burstFlying) {
    applyBurstTumbleDI(player, input);
  }

  let moveDir = 0;
  if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) moveDir -= 1;
  if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) moveDir += 1;

  // --- Airborne strafe (free, no pool check) ---
  if (moveDir !== 0 && !player.tumble && !player.grounded && !player.liftActive) {
    // Post-burst velocity air control — real trajectory change alongside position teleport
    if (player.burstFlying) {
      applyBurstAirControl(player, moveDir);
    }

    const prevAirX = player.x;
    const rawDestX = player.x + moveDir * MOVEMENT.DASH_SPEED;
    player.x = clampToWorldX(rawDestX);

    if (isPointInTerrain(player.x, player.y) ||
      isPointInTerrain(player.x + PHYSICS.ECB_RADIUS * moveDir, player.y)) {
      player.x = prevAirX;
    } else {
      player.rotation += moveDir * MOVEMENT.TILT_RATE;
    }
  }

  // Update direction tracking
  player.prevMoveDir = player.moveDir;
  player.moveDir = moveDir;

  // --- Landing detection (AFTER burst check to prevent race) ---
  if (player.grounded && !player.tumble) {
    const duration = computeLandingDuration(player.damagePercent, player.tankId);
    player.defenseSubPhase = 'landing';
    player.landingTimer = duration;
    player.landingTimerMax = duration;
  }
}

/**
 * Landing sub-phase: walk-only movement, constant timer drain, no burst/hop/lift.
 * Ends when timer depletes.
 */
function handleLandingPhase(
  state: GameState,
  pi: number,
  player: import('../types').Player,
  input: InputProvider,
  fx: GameEffect[],
): void {
  // --- Movement input (walk only, gated on timer) ---
  let moveDir = 0;
  if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) moveDir -= 1;
  if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) moveDir += 1;

  if (moveDir !== 0 && player.landingTimer > 0 && !player.tumble) {
    if (player.grounded) {
      const destX = clampToWorldX(player.x + moveDir * MOVEMENT.DASH_SPEED);
      const { nx, ny } = getSurfaceNormal(destX, player.y);
      const absNy = Math.abs(ny);

      if (absNy >= TRACTION.WALK_WALL_NY) {
        const goingUphill = moveDir * nx < 0;
        const slopeAngle = 1 - absNy;
        const rise = goingUphill
          ? MOVEMENT.MAX_CLIMBABLE_RISE * slopeAngle / (1 - TRACTION.WALK_WALL_NY)
          : 0;

        const { speedMult } = calculateMoveCost(rise);
        const effectiveSpeedMult = rise > MOVEMENT.MAX_CLIMBABLE_RISE
          ? Math.max(MOVEMENT.MIN_STEEP_SPEED_MULT, speedMult)
          : speedMult;

        const maxDist = MOVEMENT.DASH_SPEED * effectiveSpeedMult;
        const newX = clampToWorldX(player.x + moveDir * maxDist);

        player.walkDeltaX = newX - player.x;
        player.walkCatchupTimer = TRACTION.CATCHUP_FRAMES;
        if (player.actionState.tag !== 'walking') mirrorWalking(player);
        player.rotation += moveDir * MOVEMENT.TILT_RATE;
      }
    }
  }

  // Update direction tracking
  player.prevMoveDir = player.moveDir;
  player.moveDir = moveDir;

  // --- Constant drain ---
  player.landingTimer--;
  if (player.landingTimer < 0) player.landingTimer = 0;

  // --- End condition: timer depleted ---
  // Only authority resolves; non-authority receives defense_resolve message.
  if (!state.defenseWaitForAuthority) {
    if (player.landingTimer <= 0) {
      resolveDefender(state, fx);
    }
  }
}

/**
 * Resolve current defender's defense phase and activate next or transition out.
 */
function resolveDefender(state: GameState, fx: GameEffect[]): void {
  const pi = state.defenseActivePlayer;
  if (pi >= 0) {
    const p = state.players[pi];
    p.defenseSubPhase = 'none';
    // Emit defense resolved for multiplayer sync
    fx.push({
      type: 'defense_resolved',
      playerIndex: pi,
      x: p.x, y: p.y,
      vx: p.vx, vy: p.vy,
      tumble: p.tumble,
      hitstunFrames: p.hitstunFrames,
      tumbleRecoveryFrame: p.tumbleRecoveryFrame,
      tumbleRecoveryMax: p.tumbleRecoveryMax,
      momentum: p.momentum,
      weaponCharge: p.weaponCharge,
      settled: p.settled,
      grounded: p.grounded,
      liftFuel: p.liftFuel,
      liftActive: p.liftActive,
      fastFallActive: p.fastFallActive,
      landingTimer: p.landingTimer,
      landingTimerMax: p.landingTimerMax,
      defenseSubPhase: p.defenseSubPhase,
      onWireframe: p.onWireframe,
    });
  }

  // Check for more defenders
  if (state.defenseQueue.length > 0) {
    activateNextDefender(state, fx);
  } else {
    // All defenders done — unfreeze everyone, transition to awaiting
    unfreezeAllPlayers(state);
    state.defenseActivePlayer = -1;
    transitionPhase(state, 'awaiting_next_turn', fx);
    state.turnTimer = TURN_FLOW.TURN_DELAY;
  }
}

/**
 * Called when burst window completes and burstFromDefense is true.
 * Returns to defensive_movement phase instead of aiming.
 * Player is airborne post-burst — landing will trigger naturally.
 */
export function returnToDefenseFromBurst(state: GameState, fx: GameEffect[]): void {
  state.burstFromDefense = false;

  // Post-burst player is airborne — set air sub-phase so landing detection works
  const pi = state.defenseActivePlayer;
  if (pi >= 0) {
    state.players[pi].defenseSubPhase = 'air';
  }

  transitionPhase(state, 'defensive_movement', fx);
  state.freezeUntil = Math.max(
    state.freezeUntil,
    state.frameCount + msToFrames(DEFENSE.PHASE_TRANSITION_FREEZE_MS),
  );
}
